import React, { useEffect, useState, useCallback } from 'react';
import { payrollAPI, employeesAPI } from '../../lib/api';
import { toast } from 'sonner';
import { extractErrorMessage } from '../../lib/errors';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { HandCoins, Loader2, Check } from 'lucide-react';

const KINDS = [
  { v: 'hourly', label: 'Hourly' },
  { v: 'daily', label: 'Daily' },
  { v: 'flat', label: 'Flat amount' },
];

const empty = { employee_id: '', kind: 'flat', amount: '', job_number: '', date: '', reason: '' };

// Extra pay requests + approval (item 11). A request and its approval are two
// separate actions: you cannot approve your own request (enforced server-side).
export default function ExtraPayCard({ refreshKey }) {
  const [form, setForm] = useState({ ...empty });
  const [employees, setEmployees] = useState([]);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [note, setNote] = useState('');
  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [e, r] = await Promise.all([
        employeesAPI.list({ limit: 1000 }), payrollAPI.extraPayList(),
      ]);
      const list = Array.isArray(e.data) ? e.data : (e.data.employees || e.data.items || []);
      setEmployees(list);
      setRows(r.data.requests || []);
      setNote(r.data.restricted_note || '');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Could not load extra pay'));
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const submit = async () => {
    if (!form.employee_id) { toast.error('Choose an employee'); return; }
    if (!form.amount || Number(form.amount) <= 0) { toast.error('Enter an amount'); return; }
    if (!form.date) { toast.error('Enter a date'); return; }
    if (!form.reason.trim()) { toast.error('A reason is required'); return; }
    setSubmitting(true);
    try {
      await payrollAPI.extraPayCreate({ ...form, amount: Number(form.amount), job_number: form.job_number || null });
      toast.success('Extra pay request submitted — pending approval');
      setForm({ ...empty });
      load();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Could not submit the request'));
    } finally { setSubmitting(false); }
  };

  const approve = async (id) => {
    try {
      await payrollAPI.extraPayApprove(id);
      toast.success('Approved');
      load();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Could not approve'));
    }
  };

  const reject = async (id) => {
    const reason = window.prompt('Reason for rejecting this extra pay request? (optional)') ?? null;
    if (reason === null) return;
    try {
      await payrollAPI.extraPayReject(id, reason);
      toast.success('Rejected');
      load();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Could not reject'));
    }
  };

  const empName = (id) => {
    const e = employees.find((x) => x.id === id);
    return e ? `${e.employee_number || ''} · ${e.first_name} ${e.last_name}` : id;
  };

  return (
    <Card data-testid="payroll-extra-pay">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><HandCoins className="w-5 h-5" /> Extra pay requests</CardTitle>
        <CardDescription>
          One-off extra pay (hourly, daily or a flat amount) for salaried staff. Every request needs a
          separate approver — you cannot approve your own.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div className="md:col-span-2">
            <Label>Employee</Label>
            <Select value={form.employee_id} onValueChange={(v) => change('employee_id', v)}>
              <SelectTrigger data-testid="extra-pay-employee"><SelectValue placeholder="Choose…" /></SelectTrigger>
              <SelectContent>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id} data-testid={`extra-pay-emp-${e.id}`}>
                    {e.employee_number || '—'} · {e.first_name} {e.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Kind</Label>
            <Select value={form.kind} onValueChange={(v) => change('kind', v)}>
              <SelectTrigger data-testid="extra-pay-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                {KINDS.map((k) => <SelectItem key={k.v} value={k.v}>{k.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Amount</Label>
            <Input type="number" step="0.01" min="0" value={form.amount}
              onChange={(e) => change('amount', e.target.value)} data-testid="extra-pay-amount" />
          </div>
          <div>
            <Label>Date</Label>
            <Input type="date" value={form.date} onChange={(e) => change('date', e.target.value)} data-testid="extra-pay-date" />
          </div>
          <div>
            <Label>Job # <span className="text-slate-400">(optional)</span></Label>
            <Input value={form.job_number} onChange={(e) => change('job_number', e.target.value)} data-testid="extra-pay-job" />
          </div>
          <div className="md:col-span-5">
            <Label>Reason</Label>
            <Textarea rows={2} value={form.reason} onChange={(e) => change('reason', e.target.value)} data-testid="extra-pay-reason" />
          </div>
          <div>
            <Button onClick={submit} disabled={submitting} className="w-full" data-testid="extra-pay-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit request'}
            </Button>
          </div>
        </div>

        {note && <p className="text-xs text-amber-700" data-testid="extra-pay-restricted">{note}</p>}

        {busy ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-500 py-2" data-testid="extra-pay-empty">No extra pay requests yet.</p>
        ) : (
          <div className="overflow-auto max-h-96 border rounded">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Employee</TableHead><TableHead>Kind</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead>Date</TableHead>
                <TableHead>Reason</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} data-testid={`extra-pay-row-${r.id}`}>
                    <TableCell className="text-xs font-mono">{empName(r.employee_id)}</TableCell>
                    <TableCell className="text-xs capitalize">{r.kind}</TableCell>
                    <TableCell className="text-right text-sm font-medium">{r.amount === null || r.amount === undefined ? '—' : formatCurrency(r.amount)}</TableCell>
                    <TableCell className="text-xs">{formatDate(r.date)}</TableCell>
                    <TableCell className="text-xs max-w-xs truncate">{r.reason}</TableCell>
                    <TableCell>
                      {r.status === 'approved'
                        ? <Badge className="bg-emerald-600" data-testid={`extra-pay-status-${r.id}`}><Check className="w-3 h-3 mr-1" />Approved</Badge>
                        : r.status === 'rejected'
                          ? <Badge variant="destructive" data-testid={`extra-pay-status-${r.id}`}>Rejected</Badge>
                          : <Badge variant="outline" data-testid={`extra-pay-status-${r.id}`}>Pending</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      {r.status === 'pending' && (
                        <div className="flex gap-2 justify-end">
                          <Button size="sm" variant="outline" onClick={() => approve(r.id)} data-testid={`extra-pay-approve-${r.id}`}>Approve</Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => reject(r.id)} data-testid={`extra-pay-reject-${r.id}`}>Reject</Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
